import React, { useState, useEffect } from 'react';
import CustomModal from '../reusable/CustomModal';
import CustomButton from '../reusable/CustomButton';
import { executeSchoolReportWorkflowTransition, WorkflowTransitionResponse } from '../../services/workflowService';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';

interface ReportSchoolReportIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (transitionResponse: WorkflowTransitionResponse) => void;
  schoolId: string;
}

const ReportSchoolReportIssueModal = ({ isOpen, onClose, onSuccess, schoolId }: ReportSchoolReportIssueModalProps) => {
  const [issueDetails, setIssueDetails] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen } = useErrorModal();

  useEffect(() => {
    if (isOpen) {
      setIssueDetails('');
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIssueDetails('');
    setIsLoading(false);
    onClose();
  };

  const handleSend = async () => {
    if (!userDetails || !userDetails.eoaId || !userDetails.access_token || !issueDetails.trim()) return;

    try {
      setIsLoading(true);

      const metadata = {
        rejected_at: new Date().toISOString(),
        rejected_reason: issueDetails.trim(),
        rejected_by: userDetails.name || 'Unknown',
        rejected_by_role: userDetails.role || 'Unknown',
      };

      const transitionResponse = await executeSchoolReportWorkflowTransition(
        userDetails.eoaId,
        schoolId,
        userDetails.access_token,
        'reject_school_report',
        metadata
      );

      onSuccess(transitionResponse);
      handleClose();
    } catch (error) {
      handleOpen('Error occurred while reporting the issue', error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const isSendDisabled = !issueDetails.trim() || isLoading;

  return (
    <CustomModal isOpen={isOpen} handleClose={handleClose} title="Report an issue" icon={false} maxWidth="sm">
      <p className="report-issue-description">
        If there's a problem with this school report, let us know and we'll reach out to follow up.
      </p>

      <div className="report-issue-form">
        <label className="report-issue-label">
          <strong>Details of the issue</strong>
        </label>
        <textarea
          className="report-issue-textarea"
          value={issueDetails}
          onChange={(e) => setIssueDetails(e.target.value)}
          placeholder="Type here"
          rows={5}
          disabled={isLoading}
        />
      </div>

      <div className="report-issue-buttons">
        <CustomButton variant="secondary" buttonText="Cancel" buttonAction={handleClose} disabled={isLoading} />
        <CustomButton
          variant="primary"
          buttonText="Send"
          buttonAction={handleSend}
          loading={isLoading}
          disabled={isSendDisabled}
        />
      </div>
    </CustomModal>
  );
};

export default ReportSchoolReportIssueModal;
